import { useState, useRef, useCallback, useEffect } from 'react'
import {
  X, Image, Upload, Search, CheckCircle2, AlertCircle, Loader2,
  Folder, FolderOpen, ChevronLeft,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAssets, useAssetFolders, useUploadAsset, STORAGE_QUOTA_BYTES } from '@/hooks/use-assets'
import { compressToWebP } from '@/lib/image'
import type { Asset, AssetFolder } from '@/hooks/use-assets'

// ── Types ─────────────────────────────────────────────────────────────────────

interface MediaPickerProps {
  open: boolean
  onClose: () => void
  /** Called when an asset is confirmed — from library pick or after upload */
  onSelect: (assetId: string, url: string) => void
  /** When set, uploaded assets are placed inside this folder (UUID). Omit for root. */
  uploadFolderId?: string
}

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']
const MAX_FILE_SIZE = 50 * 1024 * 1024 // 50 MB

type Tab = 'library' | 'upload'

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

// ── FolderCard ────────────────────────────────────────────────────────────────

function FolderCard({ folder, onClick }: { folder: AssetFolder; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative aspect-square rounded-lg border-2 border-border hover:border-primary/50 bg-muted/40 hover:bg-accent transition-all flex flex-col items-center justify-center gap-2 p-3"
    >
      <FolderOpen className="h-10 w-10 text-muted-foreground group-hover:text-primary transition-colors" />
      <p className="text-xs font-medium text-center leading-tight line-clamp-2 w-full break-words">
        {folder.name}
      </p>
    </button>
  )
}

// ── AssetCard ─────────────────────────────────────────────────────────────────

function AssetCard({
  asset,
  selected,
  onClick,
}: {
  asset: Asset
  selected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'relative group aspect-square rounded-lg overflow-hidden border-2 transition-all',
        selected
          ? 'border-primary ring-2 ring-primary/30'
          : 'border-border hover:border-primary/50',
      )}
    >
      <img
        src={asset.file_url}
        alt={asset.alt_text ?? asset.file_name}
        className="h-full w-full object-cover"
        loading="lazy"
      />
      {selected && (
        <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
          <CheckCircle2 className="h-8 w-8 text-primary drop-shadow" />
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 bg-black/60 px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <p className="text-white text-xs truncate">{asset.file_name}</p>
        <p className="text-white/70 text-xs">{formatBytes(asset.file_size)}</p>
      </div>
    </button>
  )
}

// ── MediaPicker ───────────────────────────────────────────────────────────────

export default function MediaPicker({ open, onClose, onSelect, uploadFolderId }: MediaPickerProps) {
  const [activeTab, setActiveTab] = useState<Tab>('library')
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)

  // Library folder navigation: null = root, string uuid = inside folder
  const [libraryFolderId, setLibraryFolderId] = useState<string | null>(null)

  // Upload state
  const [dragOver, setDragOver] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [filePreview, setFilePreview] = useState<string | null>(null)
  const [altText, setAltText] = useState('')
  const [caption, setCaption] = useState('')
  const [tags, setTags] = useState('')
  const [fileError, setFileError] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)

  const fileInputRef = useRef<HTMLInputElement>(null)

  const { data: folders = [] } = useAssetFolders()

  // When searching, bypass folder filter and show all assets.
  // When not searching, filter by current folder (null = root-only).
  const isSearching = search.trim().length > 0
  const effectiveFolderId = isSearching ? undefined : libraryFolderId

  const { data: assetsData, isLoading: assetsLoading } = useAssets(undefined, effectiveFolderId)
  const uploadAsset = useUploadAsset()

  // Reset when modal opens
  useEffect(() => {
    if (open) {
      setActiveTab('library')
      setSearch('')
      setSelectedId(null)
      setLibraryFolderId(null)
      resetUpload()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Clear selection when navigating folders
  useEffect(() => {
    setSelectedId(null)
  }, [libraryFolderId])

  function resetUpload() {
    setSelectedFile(null)
    setFilePreview(null)
    setAltText('')
    setCaption('')
    setTags('')
    setFileError(null)
    setIsUploading(false)
    uploadAsset.reset()
  }

  const filteredAssets = (assetsData ?? []).filter((a) => {
    if (!isSearching) return true
    const q = search.toLowerCase()
    return (
      a.file_name.toLowerCase().includes(q) ||
      (a.alt_text ?? '').toLowerCase().includes(q) ||
      a.tags.some((t) => t.toLowerCase().includes(q))
    )
  })

  const currentFolder = folders.find((f) => f.id === libraryFolderId) ?? null

  // ── File helpers ──────────────────────────────────────────────────────────

  function handleFileSelect(file: File) {
    if (!ALLOWED_TYPES.includes(file.type)) {
      setFileError('Tipe file tidak didukung. Gunakan JPEG, PNG, WebP, atau GIF.')
      return
    }
    if (file.size > MAX_FILE_SIZE) {
      setFileError(`Ukuran file terlalu besar (maks ${formatBytes(MAX_FILE_SIZE)}).`)
      return
    }
    setFileError(null)
    setSelectedFile(file)
    setFilePreview(URL.createObjectURL(file))
    uploadAsset.reset()
  }

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) handleFileSelect(file)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Upload ────────────────────────────────────────────────────────────────

  async function handleUpload() {
    if (!selectedFile || isUploading) return
    setIsUploading(true)
    const fileToUpload = await compressToWebP(selectedFile)
    uploadAsset.mutate(
      { file: fileToUpload, alt_text: altText, caption, tags, folder_id: uploadFolderId },
      {
        onSuccess: (asset) => {
          onSelect(asset.id, asset.file_url)
          onClose()
        },
        onSettled: () => setIsUploading(false),
      },
    )
  }

  // ── Library confirm ───────────────────────────────────────────────────────

  function handleLibraryConfirm() {
    if (!selectedId) return
    const asset = (assetsData ?? []).find((a) => a.id === selectedId)
    if (asset) {
      onSelect(asset.id, asset.file_url)
      onClose()
    }
  }

  if (!open) return null

  void STORAGE_QUOTA_BYTES

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Panel */}
      <div className="relative z-10 flex flex-col w-full max-w-3xl max-h-[85vh] rounded-xl border border-border bg-card shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <h2 className="font-semibold text-lg">Pilih Media</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border shrink-0">
          {(['library', 'upload'] as Tab[]).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => { setActiveTab(tab); resetUpload() }}
              className={cn(
                'flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors',
                activeTab === tab
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              {tab === 'library' ? <Image className="h-4 w-4" /> : <Upload className="h-4 w-4" />}
              {tab === 'library' ? 'Library' : 'Upload Baru'}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">

          {/* ── Tab: Library ──────────────────────────────────────────────── */}
          {activeTab === 'library' && (
            <div className="p-4 flex flex-col gap-4">

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Cari nama file, alt text, atau tag…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>

              {/* Breadcrumb — shown when inside a folder and not searching */}
              {!isSearching && currentFolder && (
                <div className="flex items-center gap-2 -mb-1">
                  <button
                    type="button"
                    onClick={() => setLibraryFolderId(null)}
                    className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Semua
                  </button>
                  <span className="text-muted-foreground">/</span>
                  <span className="flex items-center gap-1.5 text-sm font-medium">
                    <Folder className="h-3.5 w-3.5" />
                    {currentFolder.name}
                  </span>
                </div>
              )}

              {/* Search mode: label */}
              {isSearching && (
                <p className="text-xs text-muted-foreground -mb-1">
                  Hasil pencarian di semua folder
                </p>
              )}

              {assetsLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  {/* Folder grid — only at root when not searching */}
                  {!isSearching && libraryFolderId === null && folders.length > 0 && (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                      {folders.map((folder) => (
                        <FolderCard
                          key={folder.id}
                          folder={folder}
                          onClick={() => setLibraryFolderId(folder.id)}
                        />
                      ))}
                    </div>
                  )}

                  {/* Separator between folders and root files */}
                  {!isSearching && libraryFolderId === null && folders.length > 0 && filteredAssets.length > 0 && (
                    <div className="flex items-center gap-3">
                      <div className="flex-1 border-t border-border" />
                      <span className="text-xs text-muted-foreground">File</span>
                      <div className="flex-1 border-t border-border" />
                    </div>
                  )}

                  {/* Empty states */}
                  {filteredAssets.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-2">
                      <Image className="h-12 w-12 opacity-30" />
                      <p className="text-sm">
                        {isSearching
                          ? 'Tidak ada aset yang cocok.'
                          : currentFolder
                          ? `Folder "${currentFolder.name}" masih kosong.`
                          : folders.length > 0
                          ? 'Tidak ada file di root.'
                          : 'Belum ada aset. Upload dulu!'}
                      </p>
                    </div>
                  )}

                  {/* Asset grid */}
                  {filteredAssets.length > 0 && (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                      {filteredAssets.map((asset) => (
                        <AssetCard
                          key={asset.id}
                          asset={asset}
                          selected={selectedId === asset.id}
                          onClick={() => setSelectedId(asset.id === selectedId ? null : asset.id)}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── Tab: Upload ───────────────────────────────────────────────── */}
          {activeTab === 'upload' && (
            <div className="p-6 space-y-5">
              {!selectedFile ? (
                <div
                  onDrop={handleDrop}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
                  onDragLeave={() => setDragOver(false)}
                  onClick={() => fileInputRef.current?.click()}
                  className={cn(
                    'flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed py-16 cursor-pointer transition-colors',
                    dragOver
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50 hover:bg-accent/30',
                  )}
                >
                  <Upload className="h-10 w-10 text-muted-foreground" />
                  <div className="text-center">
                    <p className="text-sm font-medium">Seret & lepas gambar ke sini</p>
                    <p className="text-xs text-muted-foreground mt-1">atau klik untuk memilih file</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      JPEG, PNG, WebP, GIF · Maks {formatBytes(MAX_FILE_SIZE)}
                    </p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ALLOWED_TYPES.join(',')}
                    className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileSelect(f) }}
                  />
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Preview */}
                  <div className="flex gap-4 items-start">
                    <div className="shrink-0 h-24 w-24 rounded-lg overflow-hidden border border-border bg-muted">
                      {filePreview && (
                        <img src={filePreview} alt="preview" className="h-full w-full object-cover" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{selectedFile.name}</p>
                      <p className="text-xs text-muted-foreground">{formatBytes(selectedFile.size)}</p>
                      <button
                        type="button"
                        onClick={resetUpload}
                        className="mt-2 text-xs text-destructive hover:underline"
                      >
                        Ganti file
                      </button>
                    </div>
                  </div>

                  {/* Metadata */}
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Alt Text <span className="text-muted-foreground font-normal">(SEO & aksesibilitas)</span>
                      </label>
                      <input
                        type="text"
                        value={altText}
                        onChange={(e) => setAltText(e.target.value)}
                        placeholder="Contoh: Foto member Sakura di konser 2025"
                        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Caption</label>
                      <input
                        type="text"
                        value={caption}
                        onChange={(e) => setCaption(e.target.value)}
                        placeholder="Contoh: Penampilan di Anime Festival 2025"
                        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Tags <span className="text-muted-foreground font-normal">(pisahkan dengan koma)</span>
                      </label>
                      <input
                        type="text"
                        value={tags}
                        onChange={(e) => setTags(e.target.value)}
                        placeholder="Contoh: member, concert, 2025"
                        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                      />
                    </div>
                  </div>
                </div>
              )}

              {fileError && (
                <div className="flex items-start gap-2 rounded-lg bg-destructive/10 border border-destructive/30 px-4 py-3 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>{fileError}</span>
                </div>
              )}

              {uploadAsset.error && (
                <div className="flex items-start gap-2 rounded-lg bg-destructive/10 border border-destructive/30 px-4 py-3 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>{uploadAsset.error.message}</span>
                </div>
              )}

              {uploadAsset.isPending && (
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Mengunggah…</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm border border-border hover:bg-accent transition-colors"
          >
            Batal
          </button>

          {activeTab === 'library' ? (
            <button
              type="button"
              disabled={!selectedId}
              onClick={handleLibraryConfirm}
              className="px-4 py-2 rounded-lg text-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Pilih Aset
            </button>
          ) : (
            <button
              type="button"
              disabled={!selectedFile || uploadAsset.isPending || isUploading}
              onClick={handleUpload}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {(uploadAsset.isPending || isUploading) && <Loader2 className="h-4 w-4 animate-spin" />}
              {uploadAsset.isPending || isUploading ? 'Mengunggah…' : 'Upload & Pilih'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
